import { ActorDatabaseContextProvider } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ContentDatabaseContextProvider } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContextProvider } from "@-/dev.improve.haniverse.contexts.database.static";
import type { PropsWithChildren } from "react";
import {
  rebuildActorSyncReplica,
  refreshActorSyncCredential,
  reopenActorSyncConnection,
  useActorSync,
} from "@-/dev.improve.haniverse.lib.sync-client";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { getAuthClient } from "@-/dev.improve.haniverse.lib.auth.client";
import { useDeviceAccount } from "@-/dev.improve.haniverse.lib.device-settings";
import { useActorTursoRemoteSync } from "@-/dev.improve.haniverse.routes.nav._lib.turso";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { View } from "@-/com.npmjs.react-native";
import { NavContext } from "#nav-context";
import { useNavContextValue } from "#nav-context-value";
import { DatabaseSupportGate } from "#database-support-gate";
import { ActorSyncPreparingContext } from "#use-is-actor-sync-preparing";
import { getCachedActorDatabase } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useActorBackups } from "@-/dev.improve.haniverse.lib.actor-backups";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";

function getBackupDatabase() {
  return getCachedActorDatabase()?.rawDatabase ?? null;
}

function ActorSyncPreparingAccessibilityTag() {
  return (
    <View
      accessibilityLabel="actor-sync-preparing"
      accessible
      className="absolute size-px opacity-0"
      importantForAccessibility="yes"
      pointerEvents="none"
    />
  );
}

function NavContextInner({ children }: PropsWithChildren) {
  const hasHydrated = useHasHydrated();
  // Provision the per-user Turso database + write the device-account record.
  const { isPreparingActorSync } = useActorSync();

  // Drive Turso replication of the on-device actor DB once a device-account
  // (provisioned above) exists. The returned `ActorRemoteSync` also reports when
  // the initial pull settles; thread it through NavContext for the readiness gate.
  const [actorConnection] = useContextSelectors(ActorDatabaseContext, (c) => c.dbConnection);
  const [deviceAccount] = useDeviceAccount();
  // The signed-in user's id scopes the per-user auto-sync Pusher channel; read it
  // here (a component that can safely use the auth client) and pass it down.
  const { data: session } = getAuthClient().useSession();
  const userId = session?.user?.id ?? null;
  // Diagnostic: confirm NavContextInner mounts + the hook's inputs.
  logger(import.meta.location).info("nav-inner:render", {
    hasActorConnection: actorConnection != null,
    hasDeviceAccount: deviceAccount != null,
    hasUserId: userId != null,
  });
  const queryClient = useQueryClient();
  const actorRemoteSync = useActorTursoRemoteSync(actorConnection, deviceAccount, userId, {
    rebuild: () => rebuildActorSyncReplica(queryClient, "sync supervisor"),
    refreshCredential: () => refreshActorSyncCredential(queryClient),
    reopen: () => reopenActorSyncConnection(queryClient),
  });
  const isActorSyncPreparing =
    isPreparingActorSync ||
    (deviceAccount != null &&
      actorRemoteSync?.isLocalReplicaReady !== true &&
      actorRemoteSync?.isInitialPullComplete !== true);

  // Maintenance still waits for the initial drain; releasing the local UI must not move
  // backfills and backup creation onto the first screen's query queue.
  const isMaintenanceReady =
    !isPreparingActorSync &&
    (deviceAccount == null || actorRemoteSync?.isInitialPullComplete === true);
  useActorBackups(getBackupDatabase, isMaintenanceReady);

  const value = useNavContextValue({ actorRemoteSync });

  return (
    <ActorSyncPreparingContext.Provider value={isActorSyncPreparing}>
      <NavContext.Provider value={value}>
        {children}
        {/* Device sync state is unavailable during SSR; keep the initial marker tree identical. */}
        {hasHydrated && isActorSyncPreparing ? <ActorSyncPreparingAccessibilityTag /> : null}
      </NavContext.Provider>
    </ActorSyncPreparingContext.Provider>
  );
}

export function NavContextProvider({ children }: PropsWithChildren) {
  return (
    <DatabaseSupportGate>
      <ActorDatabaseContextProvider>
        <ContentDatabaseContextProvider>
          <StaticDatabaseContextProvider>
            <NavContextInner>{children}</NavContextInner>
          </StaticDatabaseContextProvider>
        </ContentDatabaseContextProvider>
      </ActorDatabaseContextProvider>
    </DatabaseSupportGate>
  );
}
